import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { billingApi, integrationsApi, billingRunsApi, haloApi, BillingRunInfo, HaloCatalogueItem } from '../services/api';
import HaloItemPicker from '../components/HaloItemPicker';
import './Integrations.css';

const ZAR = (v: string | number) =>
  `R ${(typeof v === 'string' ? parseFloat(v) || 0 : v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Billed / outstanding per integration for one billing-run snapshot
interface RunIntegrationRow {
  service_type: string;
  label: string;
  lines: number;
  invoiced_lines: number;
  customers: number;
  invoiced_customers: number;
  amount: string;
  invoiced_amount: string;
  outstanding_amount: string;
  entities: number;
  outstanding_entities: number;
  complete: boolean;
  outstanding: {
    customer_id: number; customer_name: string; mapping_id: number | null;
    external_id: string | null; external_name: string | null; lines: number; amount: string; status: string;
  }[];
}

type IntTab = 'sync' | 'pricebook' | 'billing';
const initialTab = (): IntTab => {
  const t = new URLSearchParams(window.location.search).get('tab');
  return t === 'pricebook' || t === 'billing' ? t : 'sync';
};

interface SyncLogEntry {
  id: number;
  integration: string;
  trigger: string;
  status: string; // running | success | partial | error
  started_at: string | null;
  finished_at: string | null;
  duration_seconds: number | null;
  items_synced: number | null;
  message: string | null;
  details: any;
}

interface IntegrationStatus {
  integration: string;
  label: string;
  description: string;
  configured: boolean;
  env_vars: Record<string, boolean>;
  nightly_enabled: boolean;
  last_sync: SyncLogEntry | null;
  last_nightly: SyncLogEntry | null;
}

const STATUS_META: Record<string, { icon: string; cls: string; label: string }> = {
  running: { icon: '⏳', cls: 'running', label: 'Running' },
  success: { icon: '✅', cls: 'success', label: 'Success' },
  partial: { icon: '⚠️', cls: 'partial', label: 'Partial' },
  error: { icon: '❌', cls: 'error', label: 'Failed' },
};

function timeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

interface NightlySummary {
  has_run: boolean;
  message?: string;
  started_at?: string | null;
  finished_at?: string | null;
  ok?: number;
  partial?: number;
  failed?: number;
  running?: number;
  total_items?: number;
  integrations?: SyncLogEntry[];
  skipped?: { integration: string; reason: string }[];
}

interface PriceBookRow {
  id: number;
  service_type: string;
  item_name: string;
  cost_price: string | null;
  cost_source: string; // integration | manual
  cost_updated_at: string | null;
  selling_price: string | null;
  margin: string | null;
  halo_item_id: string | null;
  halo_item_name: string | null;
  affected_lines: number;
  client_prices: number;
  updated_at: string | null;
}

const PB_SERVICE_TYPES = ['cloudblue', 'cove', 'proxmox', 'whmcs', 'xero', 'sentinelone', 'splynx', 'portaone', 'redstor'];

// CloudBlue per-run change detail (which client changed which licenses)
const CHG_META: Record<string, { label: string }> = {
  increase: { label: '▲ Licenses added' },
  decrease: { label: '▼ Licenses reduced' },
  new: { label: '＋ New subscription' },
  removed: { label: '− Removed' },
  status_change: { label: 'Status change' },
};

function Integrations() {
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState<IntegrationStatus[]>([]);
  const [nightly, setNightly] = useState<NightlySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<Set<string>>(new Set());
  const [expandedLogs, setExpandedLogs] = useState<string | null>(null);
  const [logs, setLogs] = useState<SyncLogEntry[]>([]);
  const [changesFor, setChangesFor] = useState<number | null>(null);
  const [changes, setChanges] = useState<any | null>(null);
  const [changesLoading, setChangesLoading] = useState(false);
  const [showConfig, setShowConfig] = useState<string | null>(null);
  const pollTimers = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  // Price book tab
  const [tab, setTab] = useState<IntTab>(initialTab);

  // ── Billing status tab: billed / outstanding per integration for a snapshot ──
  const [runs, setRuns] = useState<BillingRunInfo[]>([]);
  const [runId, setRunId] = useState<number | null>(null);
  const [runRows, setRunRows] = useState<RunIntegrationRow[] | null>(null);
  const [runInfo, setRunInfo] = useState<BillingRunInfo | null>(null);
  const [runLoading, setRunLoading] = useState(false);
  const [openInt, setOpenInt] = useState<string | null>(null);
  const [haloPushed, setHaloPushed] = useState<number | null>(null);   // customers pushed to Halo for the run

  useEffect(() => {
    if (tab !== 'billing') return;
    (async () => {
      setRunLoading(true);
      try {
        const list = await billingRunsApi.list();
        setRuns(list);
        if (runId == null) {
          const first = list.find(r => r.status === 'open') || list[0];
          setRunId(first ? first.id : null);
          if (!first) setRunRows([]);
        }
      } catch (e: any) {
        toast.error(e.response?.data?.detail || 'Failed to load billing runs');
      } finally {
        setRunLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (tab !== 'billing' || runId == null) return;
    (async () => {
      setRunLoading(true);
      try {
        const res = await billingRunsApi.getIntegrations(runId);
        setRunRows(res.integrations);
        setRunInfo(res.run);
        try { const hs = await haloApi.runSummary(runId); setHaloPushed(hs.counts?.pushed ?? 0); } catch { setHaloPushed(null); }
      } catch (e: any) {
        toast.error(e.response?.data?.detail || 'Failed to load billing status');
      } finally {
        setRunLoading(false);
      }
    })();
  }, [tab, runId]);
  const [pbItems, setPbItems] = useState<PriceBookRow[]>([]);
  const [pbLoading, setPbLoading] = useState(false);
  const [pbImporting, setPbImporting] = useState(false);
  const [pbFilter, setPbFilter] = useState('');
  // one inline edit at a time: sell / cost / halo on a given row
  const [pbEdit, setPbEdit] = useState<{ id: number; field: 'sell' | 'cost' | 'halo' } | null>(null);
  const [pbEditValue, setPbEditValue] = useState('');
  // Halo item catalogue for the price book picker (synced into halo_items)
  const [haloItems, setHaloItems] = useState<HaloCatalogueItem[]>([]);
  const [haloItemsSyncedAt, setHaloItemsSyncedAt] = useState<string | null>(null);
  const [haloItemsBusy, setHaloItemsBusy] = useState(false);
  const loadHaloItems = useCallback(async () => {
    try { const r = await haloApi.items(); setHaloItems(r.items); setHaloItemsSyncedAt(r.synced_at); }
    catch { /* Halo not configured - the picker just shows nothing */ }
  }, []);
  const syncHaloItems = async () => {
    setHaloItemsBusy(true);
    try { const r = await haloApi.syncItems(); toast.success(`${r.items} Halo items synced`); await loadHaloItems(); }
    catch (e: any) { toast.error(e.response?.data?.detail || 'Halo item sync failed'); }
    finally { setHaloItemsBusy(false); }
  };
  // expanded client-prices sub-row
  const [pbExpanded, setPbExpanded] = useState<number | null>(null);
  const [pbClientPrices, setPbClientPrices] = useState<any[]>([]);
  const [pbCpLoading, setPbCpLoading] = useState(false);
  const [newItem, setNewItem] = useState({ service_type: 'cloudblue', item_name: '', selling_price: '' });

  const load = useCallback(async () => {
    try {
      const [data, summary] = await Promise.all([
        integrationsApi.getStatus(),
        integrationsApi.getNightlySummary().catch(() => null),
      ]);
      setStatuses(data);
      setNightly(summary);
      // resume polling for anything already running
      data.forEach((s: IntegrationStatus) => {
        if (s.last_sync?.status === 'running') startPolling(s.integration, s.last_sync.id);
      });
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    const timers = pollTimers.current;
    return () => Object.values(timers).forEach(clearInterval);
  }, [load]);

  const startPolling = (integration: string, logId: number) => {
    if (pollTimers.current[integration]) return;
    setSyncing(prev => new Set(prev).add(integration));
    pollTimers.current[integration] = setInterval(async () => {
      try {
        const log = await integrationsApi.getLog(logId);
        if (log.status !== 'running') {
          clearInterval(pollTimers.current[integration]);
          delete pollTimers.current[integration];
          setSyncing(prev => {
            const next = new Set(prev);
            next.delete(integration);
            return next;
          });
          const meta = STATUS_META[log.status] || STATUS_META.error;
          if (log.status === 'success') toast.success(`${integration}: ${log.message}`);
          else toast(`${meta.icon} ${log.message}`, { duration: 6000 });
          load();
        }
      } catch {
        /* keep polling */
      }
    }, 2500);
  };

  const handleSync = async (integration: string) => {
    try {
      const res = await integrationsApi.sync(integration);
      if (res.already_running) toast('Sync already running');
      startPolling(integration, res.log.id);
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to start sync');
    }
  };

  const handleSyncAll = async () => {
    try {
      await integrationsApi.syncAll();
      toast.success('Full sync started — cards refresh as each integration finishes');
      setTimeout(load, 3000);
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to start full sync');
    }
  };

  const toggleNightly = async (s: IntegrationStatus) => {
    try {
      await integrationsApi.updateSettings(s.integration, { nightly_enabled: !s.nightly_enabled });
      setStatuses(prev => prev.map(x =>
        x.integration === s.integration ? { ...x, nightly_enabled: !s.nightly_enabled } : x));
      toast.success(`${s.label}: nightly sync ${!s.nightly_enabled ? 'enabled' : 'disabled'}`);
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to update settings');
    }
  };

  // ── Price book: catalog with cost / default sell / client prices / Halo mapping ──
  const loadPriceBook = useCallback(async () => {
    setPbLoading(true);
    try {
      setPbItems(await billingApi.getPriceBook());
    } catch {
      toast.error('Failed to load price book');
    } finally {
      setPbLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'pricebook') { loadPriceBook(); loadHaloItems(); }
  }, [tab, loadPriceBook, loadHaloItems]);

  const savePbFields = async (row: { service_type: string; item_name: string }, fields: any,
                              okMsg = 'Saved'): Promise<boolean> => {
    try {
      const res = await billingApi.updatePriceBookItem({
        service_type: row.service_type, item_name: row.item_name, ...fields,
      });
      if (res.affected_lines === 0) {
        toast(`⚠️ Saved, but no active billing lines match "${row.item_name}"`, { duration: 6000 });
      } else {
        toast.success(okMsg);
      }
      loadPriceBook();
      return true;
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to save');
      return false;
    }
  };

  const startPbEdit = (row: PriceBookRow, field: 'sell' | 'cost' | 'halo') => {
    setPbEdit({ id: row.id, field });
    if (field === 'sell') setPbEditValue(row.selling_price || '');
    if (field === 'cost') setPbEditValue(row.cost_price || '');
    if (field === 'halo' && haloItems.length === 0) loadHaloItems();
  };

  const commitPbEdit = async (row: PriceBookRow) => {
    if (!pbEdit) return;
    const field = pbEdit.field;
    setPbEdit(null);
    if (field === 'sell') {
      await savePbFields(row, { selling_price: pbEditValue.trim() },
        pbEditValue.trim() === '' ? 'Default price cleared — lines bill at cost' : `Default price set (${row.affected_lines} line(s))`);
    } else if (field === 'cost') {
      await savePbFields(row, { cost_price: pbEditValue.trim() },
        'Cost saved — now manually maintained (won’t be overwritten by syncs)');
    }
  };

  const setPbHalo = async (row: PriceBookRow, item: HaloCatalogueItem | null) => {
    setPbEdit(null);
    await savePbFields(row, { halo_item_id: item ? String(item.halo_item_id) : '', halo_item_name: item ? item.name : '' },
      item ? `Mapped to Halo item #${item.halo_item_id} · ${item.name}` : 'Halo mapping cleared');
  };

  const revertCostToIntegration = async (row: PriceBookRow) => {
    await savePbFields(row, { cost_source: 'integration' },
      'Cost reverted to integration — refreshed from synced lines');
  };

  const addPbItem = async () => {
    if (!newItem.item_name.trim()) {
      toast.error('Item name is required');
      return;
    }
    const ok = await savePbFields(
      { service_type: newItem.service_type, item_name: newItem.item_name.trim() },
      newItem.selling_price.trim() ? { selling_price: newItem.selling_price.trim() } : {},
      'Item added to the price book');
    if (ok) setNewItem({ ...newItem, item_name: '', selling_price: '' });
  };

  const removePbItem = async (row: PriceBookRow) => {
    if (!window.confirm(`Remove "${row.item_name}" from the price book? ` +
      `${row.client_prices ? `${row.client_prices} client price(s) will be deleted and ` : ''}` +
      `${row.affected_lines} line(s) revert to the synced cost price.`)) return;
    try {
      await billingApi.deletePriceBookItem(row.service_type, row.item_name);
      toast.success('Removed from price book');
      if (pbExpanded === row.id) setPbExpanded(null);
      loadPriceBook();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  const importCloudBlue = async () => {
    setPbImporting(true);
    try {
      const res = await billingApi.importPriceBook('cloudblue');
      toast.success(`CloudBlue catalog imported: ${res.created} new item(s), ${res.cost_updated} cost(s) updated, ${res.items_seen} seen`);
      loadPriceBook();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setPbImporting(false);
    }
  };

  const togglePbClientPrices = async (row: PriceBookRow) => {
    if (pbExpanded === row.id) {
      setPbExpanded(null);
      return;
    }
    setPbExpanded(row.id);
    setPbClientPrices([]);
    setPbCpLoading(true);
    try {
      setPbClientPrices(await billingApi.getClientPrices(row.service_type, row.item_name));
    } catch {
      toast.error('Failed to load client prices');
      setPbExpanded(null);
    } finally {
      setPbCpLoading(false);
    }
  };

  const removeClientPrice = async (row: PriceBookRow, cp: any) => {
    try {
      await billingApi.setClientPrice({
        service_type: row.service_type, item_name: row.item_name,
        customer_id: cp.customer_id, selling_price: '',
      });
      toast.success(`${cp.customer_name} back to the default price`);
      setPbClientPrices(prev => prev.filter(x => x.customer_id !== cp.customer_id));
      loadPriceBook();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to remove client price');
    }
  };

  const toggleLogs = async (integration: string) => {
    setChangesFor(null);
    setChanges(null);
    if (expandedLogs === integration) {
      setExpandedLogs(null);
      return;
    }
    try {
      const data = await integrationsApi.getLogs(integration, 25);
      setLogs(data);
      setExpandedLogs(integration);
    } catch {
      toast.error('Failed to load sync history');
    }
  };

  // CloudBlue: expand a sync run to see which client changed which licenses
  const toggleChanges = async (logId: number) => {
    if (changesFor === logId) {
      setChangesFor(null);
      setChanges(null);
      return;
    }
    setChangesFor(logId);
    setChanges(null);
    setChangesLoading(true);
    try {
      setChanges(await integrationsApi.getLogChanges(logId));
    } catch {
      toast.error('Failed to load change details');
      setChangesFor(null);
    } finally {
      setChangesLoading(false);
    }
  };

  if (loading) {
    return <div className="integrations-page"><div className="loading">Loading integrations…</div></div>;
  }

  const configured = statuses.filter(s => s.configured).length;

  return (
    <div className="integrations-page">
      <div className="page-header">
        <div>
          <h1>🔌 Integrations</h1>
          <p className="page-subtitle">
            {tab === 'sync'
              ? `${configured}/${statuses.length} configured · nightly sync runs at 02:00 SAST`
              : tab === 'billing'
                ? 'Per integration: what is in the month snapshot, what has been ticked processed, and what is still outstanding'
                : 'Org-wide selling prices per item — cost is synced from the integration, sell is what we bill'}
          </p>
        </div>
        {tab === 'sync' && (
          <button className="int-btn primary" onClick={handleSyncAll}>🔄 Sync all now</button>
        )}
      </div>

      <div className="int-tabs">
        <button className={`int-tab ${tab === 'sync' ? 'active' : ''}`} onClick={() => setTab('sync')}>
          🔌 Sync & Health
        </button>
        <button className={`int-tab ${tab === 'pricebook' ? 'active' : ''}`} onClick={() => setTab('pricebook')}>
          🏷️ Price book
        </button>
        <button className={`int-tab ${tab === 'billing' ? 'active' : ''}`} onClick={() => setTab('billing')}>
          🧾 Billing status
        </button>
      </div>

      {tab === 'billing' && (
        <div className="pb-panel rb-panel">
          <div className="rb-head">
            <label className="rb-pick">
              Billing snapshot
              <select value={runId ?? ''} onChange={e => { setRunId(e.target.value ? Number(e.target.value) : null); setOpenInt(null); }}>
                {runs.length === 0 && <option value="">— no billing runs yet —</option>}
                {runs.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.period_label} · {r.status === 'open' ? (r.complete ? 'all processed' : `${r.invoiced_count}/${r.customer_count} processed`) : r.status}
                  </option>
                ))}
              </select>
            </label>
            {runInfo && runInfo.status === 'open' && (
              <span className="rb-summary">
                Taken {new Date(runInfo.snapshot_at || '').toLocaleString()} · {runInfo.customer_count} customers ·{' '}
                {ZAR(runInfo.grand_total)} / month · <strong>{ZAR(runInfo.outstanding_total)}</strong> still to invoice
                {haloPushed != null && <> · ⬆ <strong>{haloPushed}</strong> of {runInfo.customer_count} pushed to Halo</>}
              </span>
            )}
            <button className="int-btn sm" onClick={() => navigate(`/billing${runInfo ? `?period=${runInfo.period}` : ''}`)}>
              💳 Invoice Prep →
            </button>
          </div>
          <p className="pb-hint">
            For every integration in the snapshot: how much is in the month, how much of it has been ticked off as
            processed in Invoice Prep, and which mapped entities are still outstanding — so you can confirm every source got billed.
          </p>
          {runs.length === 0 && !runLoading && (
            <p className="rb-empty">No billing runs yet. Go to Invoice Prep and click <strong>Bill now for the month</strong> to sync everything and take the first snapshot.</p>
          )}
          {runRows && runRows.length > 0 && (
            <table className="rb-table">
              <thead>
                <tr>
                  <th>Integration</th>
                  <th className="num">Entities</th>
                  <th className="num">Lines</th>
                  <th className="num">Customers</th>
                  <th className="num">In snapshot</th>
                  <th className="num">Invoiced</th>
                  <th className="num">Outstanding</th>
                  <th>Progress</th>
                </tr>
              </thead>
              <tbody>
                {runRows.map(row => {
                  const pct = parseFloat(row.amount) > 0
                    ? Math.round((parseFloat(row.invoiced_amount) / parseFloat(row.amount)) * 100)
                    : (row.complete ? 100 : 0);
                  const isOpen = openInt === row.service_type;
                  return (
                    <React.Fragment key={row.service_type}>
                      <tr className={`rb-row ${row.complete ? 'done' : ''} ${isOpen ? 'open' : ''}`}
                          onClick={() => setOpenInt(isOpen ? null : row.service_type)}>
                        <td className="rb-label">{row.complete ? '✅' : '🧾'} {row.label}</td>
                        <td className="num">{row.entities}</td>
                        <td className="num">{row.invoiced_lines}/{row.lines}</td>
                        <td className="num">{row.invoiced_customers}/{row.customers}</td>
                        <td className="num">{ZAR(row.amount)}</td>
                        <td className="num">{ZAR(row.invoiced_amount)}</td>
                        <td className={`num ${row.complete ? '' : 'rb-out'}`}>{row.complete ? '—' : ZAR(row.outstanding_amount)}</td>
                        <td className="rb-progress-cell">
                          <div className="rb-progress" title={`${pct}% of the ${row.label} amount ticked processed`}>
                            <div className={`rb-progress-fill ${row.complete ? 'done' : ''}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="rb-pct">{pct}%</span>
                          {!row.complete && <span className="rb-toggle">{isOpen ? '▲' : '▼'} {row.outstanding_entities} outstanding</span>}
                        </td>
                      </tr>
                      {isOpen && !row.complete && (
                        <tr className="rb-detail">
                          <td colSpan={8}>
                            <table className="rb-outstanding">
                              <thead>
                                <tr><th>Customer</th><th>{row.service_type === 'manual' ? 'Service' : 'Entity in integration'}</th><th className="num">Lines</th><th className="num">R / month</th><th></th></tr>
                              </thead>
                              <tbody>
                                {row.outstanding.map((e, i) => (
                                  <tr key={i}>
                                    <td>{e.customer_name}</td>
                                    <td className="rb-ext">{e.external_name || e.external_id || '—'}</td>
                                    <td className="num">{e.lines}</td>
                                    <td className="num">{ZAR(e.amount)}</td>
                                    <td>
                                      <button className="int-btn sm" onClick={ev => { ev.stopPropagation(); navigate(`/billing?period=${runInfo?.period || ''}&customer=${e.customer_id}`); }}>
                                        Open in Invoice Prep
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
          {runRows && runRows.length === 0 && runs.length > 0 && !runLoading && (
            <p className="rb-empty">This run has no billable lines{runInfo && runInfo.status !== 'open' ? ` (status: ${runInfo.status})` : ''}.</p>
          )}
        </div>
      )}

      {tab === 'pricebook' && (
        <div className="pb-panel">
          <div className="pb-add">
            <select
              value={newItem.service_type}
              onChange={e => setNewItem({ ...newItem, service_type: e.target.value })}
            >
              {PB_SERVICE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input
              placeholder="Item name — must match the synced product name exactly"
              value={newItem.item_name}
              onChange={e => setNewItem({ ...newItem, item_name: e.target.value })}
              onKeyDown={e => { if (e.key === 'Enter') addPbItem(); }}
            />
            <input
              className="pb-price"
              placeholder="Default sell (opt.)"
              value={newItem.selling_price}
              onChange={e => setNewItem({ ...newItem, selling_price: e.target.value })}
              onKeyDown={e => { if (e.key === 'Enter') addPbItem(); }}
            />
            <button className="int-btn primary sm" onClick={addPbItem}>+ Add</button>
            <button className="int-btn sm" onClick={importCloudBlue} disabled={pbImporting}
                    title="Pull every distinct CloudBlue item into the catalog and refresh costs (also runs after every CloudBlue sync)">
              {pbImporting ? '⏳ Importing…' : '⬇ Import CloudBlue items'}
            </button>
            <button className="int-btn sm" onClick={loadPriceBook}>↻ Refresh</button>
            <button className="int-btn sm" onClick={syncHaloItems} disabled={haloItemsBusy}
                    title={`Pull the whole HaloPSA item catalogue into CloudView so the Halo item column can be searched${haloItemsSyncedAt ? ` (last synced ${timeAgo(haloItemsSyncedAt)})` : ''}`}>
              {haloItemsBusy ? '⏳ Syncing…' : `⬇ Sync Halo items${haloItems.length ? ` (${haloItems.length})` : ''}`}
            </button>
            <input
              className="pb-filter"
              placeholder="🔍 Filter items…"
              value={pbFilter}
              onChange={e => setPbFilter(e.target.value)}
            />
          </div>
          <p className="pb-hint">
            <strong>Cost</strong> auto-refreshes from the source integration after every sync (🔄); click a cost to
            maintain it manually (✍), ↺ hands it back to the integration. <strong>Default sell</strong> applies to all
            clients; <strong>👤 client prices</strong> override it per client (set from the Invoice Prep grid).
            <strong> Halo item</strong> maps this line to a HaloPSA item for the recurring-invoice automation — click the cell and type a name, group or Xero code to pick from the synced catalogue.
          </p>
          {pbLoading ? (
            <div className="loading">Loading price book…</div>
          ) : pbItems.length === 0 ? (
            <div className="pb-empty">
              Price book is empty — hit <strong>⬇ Import CloudBlue items</strong> to pull the whole CloudBlue catalog
              in with live cost prices, then fill in your selling prices.
            </div>
          ) : (
            <table className="pb-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Item</th>
                  <th className="num" title="What the item costs us — synced from the integration or manually maintained">Cost</th>
                  <th className="num" title="Default selling price for all clients — blank bills at cost">Default sell</th>
                  <th className="num">Margin /unit</th>
                  <th className="num" title="Client-specific prices (win over the default for that client)">👤 Clients</th>
                  <th title="HaloPSA item mapping for recurring-invoice automation">Halo item</th>
                  <th className="num" title="Active billing lines with this item">Lines</th>
                  <th>Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pbItems
                  .filter(o => !pbFilter
                    || o.item_name.toLowerCase().includes(pbFilter.toLowerCase())
                    || o.service_type.includes(pbFilter.toLowerCase()))
                  .map(o => (
                  <React.Fragment key={o.id}>
                    <tr>
                      <td className="pb-svc">{o.service_type}</td>
                      <td className="pb-item" title={o.item_name}>{o.item_name}</td>
                      <td className="num pb-cost">
                        {pbEdit?.id === o.id && pbEdit.field === 'cost' ? (
                          <input
                            className="pb-price-input" autoFocus value={pbEditValue}
                            onChange={e => setPbEditValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') commitPbEdit(o);
                              if (e.key === 'Escape') setPbEdit(null);
                            }}
                            onBlur={() => setPbEdit(null)}
                          />
                        ) : (
                          <>
                            <span
                              className="pb-cell-edit"
                              onClick={() => startPbEdit(o, 'cost')}
                              title={o.cost_source === 'manual'
                                ? 'Manually maintained cost — click to change'
                                : `Auto-refreshed from the ${o.service_type} sync${o.cost_updated_at ? ` (${timeAgo(o.cost_updated_at)})` : ''} — click to take over manually`}
                            >
                              {o.cost_price ? `R ${o.cost_price}` : <span className="pb-unset">set…</span>}
                            </span>
                            <span className={`pb-src ${o.cost_source}`}
                                  title={o.cost_source === 'manual' ? 'Manually maintained' : 'Synced from integration'}>
                              {o.cost_source === 'manual' ? '✍' : '🔄'}
                            </span>
                            {o.cost_source === 'manual' && (
                              <button className="pb-mini" title="Revert to the integration-synced cost"
                                      onClick={() => revertCostToIntegration(o)}>↺</button>
                            )}
                          </>
                        )}
                      </td>
                      <td className="num">
                        {pbEdit?.id === o.id && pbEdit.field === 'sell' ? (
                          <input
                            className="pb-price-input" autoFocus value={pbEditValue}
                            onChange={e => setPbEditValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') commitPbEdit(o);
                              if (e.key === 'Escape') setPbEdit(null);
                            }}
                            onBlur={() => setPbEdit(null)}
                          />
                        ) : (
                          <span className="pb-sell" onClick={() => startPbEdit(o, 'sell')}
                                title="Default selling price — click to change; clear to bill at cost">
                            {o.selling_price ? `R ${o.selling_price}` : <span className="pb-unset">at cost</span>}
                          </span>
                        )}
                      </td>
                      <td className="num">{o.margin ? `R ${o.margin}` : '—'}</td>
                      <td className="num">
                        <button className={`pb-cp-btn ${pbExpanded === o.id ? 'open' : ''}`}
                                onClick={() => togglePbClientPrices(o)}
                                title="Show client-specific prices">
                          {o.client_prices > 0 ? `👤 ${o.client_prices}` : '＋'}
                        </button>
                      </td>
                      <td className="pb-halo">
                        {pbEdit?.id === o.id && pbEdit.field === 'halo' ? (
                          <span className="pb-halo-edit">
                            <HaloItemPicker value={o.halo_item_id} valueName={o.halo_item_name} items={haloItems} autoFocus
                                            onSelect={item => setPbHalo(o, item)}
                                            onClear={o.halo_item_id ? () => setPbHalo(o, null) : undefined}
                                            onCancel={() => setPbEdit(null)} />
                          </span>
                        ) : (
                          <span className="pb-cell-edit" onClick={() => startPbEdit(o, 'halo')}
                                title="HaloPSA item this maps to — feeds the recurring-invoice automation">
                            {o.halo_item_id
                              ? <>#{o.halo_item_id}{o.halo_item_name ? <span className="pb-halo-nm"> · {o.halo_item_name}</span> : ''}</>
                              : <span className="pb-unset">map…</span>}
                          </span>
                        )}
                      </td>
                      <td className="num">{o.affected_lines}</td>
                      <td className="pb-when" title={o.updated_at || ''}>{timeAgo(o.updated_at)}</td>
                      <td className="ctr">
                        <button className="int-btn sm" title="Remove from price book — lines revert to cost"
                                onClick={() => removePbItem(o)}>🗑</button>
                      </td>
                    </tr>
                    {pbExpanded === o.id && (
                      <tr className="pb-cp-row">
                        <td colSpan={10}>
                          {pbCpLoading ? (
                            <span className="pb-cp-empty">⏳ Loading client prices…</span>
                          ) : pbClientPrices.length === 0 ? (
                            <span className="pb-cp-empty">
                              No client-specific prices for this item. Set one from the Invoice Prep grid — edit the
                              Sell cell on that client's line and tick "👤 this client only".
                            </span>
                          ) : (
                            <span className="pb-cp-list">
                              {pbClientPrices.map(cp => (
                                <span key={cp.customer_id} className="pb-cp-chip">
                                  {cp.customer_name}: <strong>R {cp.selling_price}</strong>
                                  <button title="Remove — back to the default price"
                                          onClick={() => removeClientPrice(o, cp)}>✕</button>
                                </span>
                              ))}
                            </span>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'sync' && (
      <>
      {/* Last nightly run banner */}
      <div className={`nightly-banner ${nightly?.has_run
        ? ((nightly.failed || 0) > 0 ? 'bad' : (nightly.partial || 0) > 0 ? 'warn' : 'ok')
        : 'none'}`}>
        {nightly?.has_run ? (
          <>
            <span className="nightly-title">
              🌙 Last nightly run — {nightly.started_at ? new Date(nightly.started_at).toLocaleString() : '?'}
            </span>
            <span className="nightly-counts">
              ✅ {nightly.ok || 0} ok
              {(nightly.partial || 0) > 0 && <> · ⚠️ {nightly.partial} partial</>}
              {(nightly.failed || 0) > 0 && <> · ❌ {nightly.failed} failed</>}
              {(nightly.running || 0) > 0 && <> · ⏳ {nightly.running} running</>}
              {' '}· {(nightly.total_items || 0).toLocaleString()} items
              {nightly.skipped && nightly.skipped.length > 0 && (
                <span className="nightly-skipped" title={nightly.skipped.map(s => `${s.integration}: ${s.reason}`).join(', ')}>
                  {' '}· {nightly.skipped.length} skipped
                </span>
              )}
            </span>
            <span className="nightly-chips">
              {(nightly.integrations || []).map(l => (
                <span key={l.id}
                      className={`nightly-chip ${(STATUS_META[l.status] || STATUS_META.error).cls}`}
                      title={`${l.message || l.status}${l.duration_seconds != null ? ` (${l.duration_seconds.toFixed(0)}s)` : ''}`}>
                  {(STATUS_META[l.status] || STATUS_META.error).icon} {l.integration}
                </span>
              ))}
            </span>
          </>
        ) : (
          <span className="nightly-title">🌙 No nightly run recorded yet — next run tonight at 02:00 SAST</span>
        )}
      </div>

      <div className="int-grid">
        {statuses.map(s => {
          const last = s.last_sync;
          const meta = last ? (STATUS_META[last.status] || STATUS_META.error) : null;
          const isSyncing = syncing.has(s.integration) || last?.status === 'running';
          return (
            <div key={s.integration} className={`int-card ${!s.configured ? 'unconfigured' : ''}`}>
              <div className="int-card-head">
                <div>
                  <span className={`int-dot ${s.configured ? 'ok' : 'off'}`}
                        title={s.configured ? 'Credentials configured' : 'Missing credentials'} />
                  <span className="int-label">{s.label}</span>
                </div>
                <label className="int-toggle" title="Include in the 02:00 nightly sync">
                  <input type="checkbox" checked={s.nightly_enabled}
                         onChange={() => toggleNightly(s)} />
                  <span>nightly</span>
                </label>
              </div>
              <div className="int-desc">{s.description}</div>

              <div className="int-last">
                {last ? (
                  <>
                    <span className={`int-status ${meta!.cls}`}>{meta!.icon} {meta!.label}</span>
                    <span className="int-when" title={last.started_at || ''}>
                      {timeAgo(last.started_at)} · {last.trigger}
                      {last.duration_seconds != null && ` · ${last.duration_seconds.toFixed(0)}s`}
                    </span>
                  </>
                ) : (
                  <span className="int-status none">No syncs recorded yet</span>
                )}
              </div>
              {last?.message && <div className={`int-message ${meta!.cls}`}>{last.message}</div>}

              <div className="int-actions">
                <button className="int-btn primary sm" disabled={!s.configured || isSyncing}
                        onClick={() => handleSync(s.integration)}>
                  {isSyncing ? '⏳ Syncing…' : '🔄 Refresh now'}
                </button>
                <button className="int-btn sm" onClick={() => toggleLogs(s.integration)}>
                  {expandedLogs === s.integration ? 'Hide history' : 'History'}
                </button>
                <button className="int-btn sm" onClick={() =>
                  setShowConfig(showConfig === s.integration ? null : s.integration)}>
                  Config
                </button>
              </div>

              {showConfig === s.integration && (
                <div className="int-config">
                  <div className="int-config-title">Credentials (server .env)</div>
                  {Object.entries(s.env_vars).map(([k, present]) => (
                    <div key={k} className="int-env-row">
                      <code>{k}</code>
                      <span className={present ? 'env-ok' : 'env-missing'}>
                        {present ? '✓ set' : '✗ missing'}
                      </span>
                    </div>
                  ))}
                  <div className="int-config-note">
                    Credentials are managed in <code>/opt/CloudViewMSP/.env</code> on the server.
                  </div>
                </div>
              )}

              {expandedLogs === s.integration && (
                <div className="int-logs">
                  {logs.length === 0 ? (
                    <div className="int-logs-empty">No sync history</div>
                  ) : (
                    <table>
                      <thead>
                        <tr><th>When</th><th>Trigger</th><th>Status</th><th>Items</th><th>Result</th></tr>
                      </thead>
                      <tbody>
                        {logs.map(l => {
                          const m = STATUS_META[l.status] || STATUS_META.error;
                          const canExpand = s.integration === 'cloudblue';
                          return (
                            <React.Fragment key={l.id}>
                              <tr
                                className={`log-${m.cls} ${canExpand ? 'log-expandable' : ''}`}
                                onClick={canExpand ? () => toggleChanges(l.id) : undefined}
                                title={canExpand ? 'Click to see which client changed which licenses in this run' : undefined}
                              >
                                <td title={l.started_at || ''}>
                                  {canExpand && <span className="log-chevron">{changesFor === l.id ? '▾' : '▸'}</span>}
                                  {timeAgo(l.started_at)}
                                </td>
                                <td>{l.trigger}</td>
                                <td>{m.icon} {m.label}</td>
                                <td className="num">{l.items_synced ?? '—'}</td>
                                <td className="log-msg">{l.message}</td>
                              </tr>
                              {canExpand && changesFor === l.id && (
                                <tr className="log-changes-row">
                                  <td colSpan={5}>
                                    {changesLoading ? (
                                      <div className="cbchg-loading">⏳ Loading change details…</div>
                                    ) : !changes || !changes.supported ? (
                                      <div className="cbchg-empty">{changes?.message || 'No change detail available for this run'}</div>
                                    ) : changes.total === 0 ? (
                                      <div className="cbchg-empty">
                                        No license changes in this run — all quantities and statuses matched the previous sync.
                                      </div>
                                    ) : (
                                      <>
                                        <div className="cbchg-summary">
                                          <span>{changes.total} change{changes.total !== 1 ? 's' : ''} detected:</span>
                                          {Object.entries(changes.counts as Record<string, number>).map(([k, v]) => (
                                            <span key={k} className={`cbchg-badge ${k}`}>
                                              {(CHG_META[k]?.label || k)} × {v}
                                            </span>
                                          ))}
                                        </div>
                                        <table className="cbchg-table">
                                          <thead>
                                            <tr>
                                              <th>Client</th>
                                              <th>License / subscription</th>
                                              <th>Change</th>
                                              <th>Old → New</th>
                                              <th className="num">Δ Qty</th>
                                              <th className="num">Qty now</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {changes.changes.map((c: any) => (
                                              <tr key={c.id}>
                                                <td className="cbchg-client">
                                                  {c.cloudview_customer_id ? (
                                                    <button
                                                      className="link-like"
                                                      title="Open the client's full view"
                                                      onClick={e => {
                                                        e.stopPropagation();
                                                        navigate(`/customers/${c.cloudview_customer_id}/services`);
                                                      }}
                                                    >
                                                      {c.client_name}
                                                    </button>
                                                  ) : (
                                                    <>
                                                      {c.client_name}
                                                      {!c.client_mapped && (
                                                        <span className="cbchg-unmapped" title="This CloudBlue account is not mapped to a CloudView customer"> ⚠ unmapped</span>
                                                      )}
                                                    </>
                                                  )}
                                                </td>
                                                <td className="cbchg-license" title={c.license || ''}>
                                                  {c.entity_type === 'customer' ? <em>customer account</em> : c.license}
                                                  {c.subscription_id && <span className="cbchg-subid"> #{c.subscription_id}</span>}
                                                </td>
                                                <td>
                                                  <span className={`cbchg-badge ${c.change_type}`}>
                                                    {CHG_META[c.change_type]?.label || c.change_type}
                                                  </span>
                                                </td>
                                                <td className="cbchg-vals">
                                                  {c.old_value != null || c.new_value != null
                                                    ? `${c.old_value ?? '—'} → ${c.new_value ?? '—'}`
                                                    : c.change_type === 'new'
                                                      ? 'added to CloudBlue'
                                                      : c.change_type === 'removed'
                                                        ? 'gone from CloudBlue'
                                                        : '—'}
                                                </td>
                                                <td className={`num cbchg-delta ${(c.delta ?? 0) > 0 ? 'pos' : (c.delta ?? 0) < 0 ? 'neg' : ''}`}>
                                                  {c.delta != null ? (c.delta > 0 ? `+${c.delta}` : c.delta) : ''}
                                                </td>
                                                <td className="num">{c.quantity_now ?? ''}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>
      )}
    </div>
  );
}

export default Integrations;
